import React, { useState, useEffect, useRef } from 'react';
import { Search, AlertCircle, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useFM } from '@/context/FMContext';
import { useFMCompliance } from '@/hooks/useFMCompliance';
import ComplianceMemberCard from '@/components/fm/ComplianceMemberCard';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PullToRefresh from 'react-simple-pull-to-refresh';

const FILTER_TABS = [
  { value: 'due_today', label: 'Due Today' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'all', label: 'All' },
];

/**
 * FMCompliance - Member Compliance Tracker with smart filtering.
 * Default view shows only members due today (filter-first approach).
 * Members grouped by assigned trainer with compliance percentages.
 */
export default function FMCompliance() {
  const { selectedGymId } = useFM();
  const [filter, setFilter] = useState('due_today');
  const [trainerId, setTrainerId] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  // Collapse state for trainer groups
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Debounce search input (300ms)
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  const {
    groups,
    overdueCount,
    summary,
    trainers,
    loading,
    error,
    refetch,
    completeMilestone,
    reassignMember,
  } = useFMCompliance(
    selectedGymId,
    debouncedSearch ? 'all' : filter,
    trainerId,
    debouncedSearch || null
  );

  // When search is active, override filter to 'all' to find any member
  const effectiveFilter = debouncedSearch ? 'all' : filter;

  // Default collapse behavior: collapse when > 3 groups
  useEffect(() => {
    if (groups.length > 3) {
      const collapsed = {};
      groups.forEach((g) => {
        collapsed[g.trainer_id] = true;
      });
      setCollapsedGroups(collapsed);
    } else {
      setCollapsedGroups({});
    }
  }, [groups.length]);

  const toggleGroup = (tId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [tId]: !prev[tId],
    }));
  };

  // Get unique trainers from groups for the filter dropdown
  const trainerOptions = groups.map((g) => ({
    trainer_id: g.trainer_id,
    name: g.trainer_name,
  }));

  // No gym selected
  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Select a gym to view compliance data</p>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={refetch} pullingContent="" refreshingContent={<RefreshingSpinner />}>
      <div className="space-y-4">
        {/* Page header */}
        <div>
          <h2 className="text-lg font-bold text-slate-800">Compliance Tracker</h2>
          <p className="text-xs text-slate-500 mt-0.5">Track member milestones and compliance</p>
        </div>

        {/* 1. Overdue banner (persistent, red) */}
        {overdueCount > 0 && (
          <button
            onClick={() => setFilter('overdue')}
            className="w-full bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-left active:scale-[0.99] transition-transform"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm font-medium">
                {overdueCount} overdue: {summary.total_overdue} milestone{summary.total_overdue !== 1 ? 's' : ''} need attention
              </span>
            </div>
          </button>
        )}

        {/* 2. Filter tabs */}
        <Tabs value={effectiveFilter} onValueChange={(val) => { setFilter(val); setSearch(''); }}>
          <TabsList className="w-full grid grid-cols-4">
            {FILTER_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="text-xs relative">
                {tab.label}
                {tab.value === 'overdue' && overdueCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {overdueCount > 9 ? '9+' : overdueCount}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* 3. Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search member by name..."
            className="w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
            >
              Clear
            </button>
          )}
        </div>

        {/* 4. Trainer filter dropdown */}
        {trainerOptions.length > 1 && !debouncedSearch && (
          <Select
            value={trainerId || 'all_trainers'}
            onValueChange={(val) => setTrainerId(val === 'all_trainers' ? null : val)}
          >
            <SelectTrigger className="h-10 text-sm">
              <SelectValue placeholder="All Trainers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_trainers">All Trainers</SelectItem>
              {trainerOptions.map((t) => (
                <SelectItem key={t.trainer_id} value={t.trainer_id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Loading state */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-4 bg-white animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
                <div className="h-3 bg-slate-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={refetch}
              className="mt-2 text-xs text-red-600 underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* 5. Content: Trainer-grouped member list */}
        {!loading && !error && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <span className="text-green-500 text-lg">&#10003;</span>
            </div>
            <p className="text-sm font-medium text-slate-700">
              {debouncedSearch
                ? 'No members match your search'
                : filter === 'due_today'
                ? 'No milestones due today'
                : filter === 'overdue'
                ? 'No overdue milestones'
                : 'No compliance items found'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {filter === 'due_today' && !debouncedSearch
                ? 'All caught up!'
                : 'Try changing filters'}
            </p>
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="space-y-3">
            {groups.map((group) => {
              const isCollapsed = collapsedGroups[group.trainer_id];
              return (
                <div
                  key={group.trainer_id}
                  className="rounded-xl border bg-white overflow-hidden"
                >
                  {/* Trainer group header */}
                  <button
                    onClick={() => toggleGroup(group.trainer_id)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 active:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-slate-700 truncate">
                        {group.trainer_name || 'Unassigned'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          group.compliance_pct >= 80
                            ? 'bg-green-100 text-green-700'
                            : group.compliance_pct >= 50
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {group.compliance_pct}%
                      </span>
                      {isCollapsed ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {/* Member cards */}
                  {!isCollapsed && (
                    <div className="p-3 space-y-3">
                      {group.members.map((member) => (
                        <ComplianceMemberCard
                          key={member.member_id}
                          member={{ ...member, trainer_name: group.trainer_name }}
                          trainers={trainers}
                          onCompleteMilestone={completeMilestone}
                          onReassign={reassignMember}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 6. Summary stats at bottom */}
        {!loading && !error && (
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500 py-2">
            <span>Total Due: {summary.total_due}</span>
            <span className="text-slate-300">|</span>
            <span>Overdue: {summary.total_overdue}</span>
            <span className="text-slate-300">|</span>
            <span>Compliance Rate: {summary.compliance_rate}%</span>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}

/**
 * Simple refreshing spinner for pull-to-refresh.
 */
function RefreshingSpinner() {
  return (
    <div className="flex justify-center py-3">
      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
